import { ApiError } from 'src/error/ApiError';
import { recordedAmount } from '../form/amountFields';
import { joinNames } from '../names';
import { GroupMember } from '../grouping';
import { Allocation, SHARE_KEY } from '../redesign/modal/allocations';
import { CashflowOwner, IncomeOwner } from './ownerTypes';
import { externalOwners, shareInPounds } from './owners';

/** The rows a form opens with: one per client owner, with their share and own figures. */
export function ownerSeed(
  owners: (CashflowOwner & { amountPostTax?: number | null })[] | undefined,
) {
  return (owners ?? [])
    .filter((owner): owner is typeof owner & { clientId: string } => !!owner.clientId)
    .map(owner => ({
      clientId: owner.clientId,
      amounts: {
        [SHARE_KEY]: owner.ownershipPercentage,
        ...(owner.amountPostTax !== undefined ? { amountPostTax: owner.amountPostTax } : {}),
      },
    }));
}

/**
 * The owners a save names: a client per row, and anyone named who is not a client kept as they
 * were, since the form has no way to change them.
 */
export function ownersFrom(
  allocations: Allocation[],
  stored: CashflowOwner[] | undefined,
): CashflowOwner[] {
  return [
    ...allocations.map(allocation => ({
      clientId: allocation.clientId,
      externalName: null,
      ownershipPercentage: recordedAmount(allocation.amounts[SHARE_KEY]),
    })),
    ...externalOwners(stored),
  ];
}

export function incomeOwnersFrom(
  allocations: Allocation[],
  stored: IncomeOwner[] | undefined,
): IncomeOwner[] {
  return [
    ...allocations.map(allocation => ({
      clientId: allocation.clientId,
      externalName: null,
      ownershipPercentage: recordedAmount(allocation.amounts[SHARE_KEY]),
      amountPostTax: recordedAmount(allocation.amounts.amountPostTax),
    })),
    ...externalOwners(stored),
  ];
}

/**
 * A person's after-tax amount is their own take-home from the income, so it cannot come to more
 * than their share of it before tax, or than the whole of it where shares are not recorded.
 */
export function postTaxError(
  allocation: Allocation,
  key: string,
  amountPreTax: number | null,
): string | undefined {
  const postTax = allocation.amounts.amountPostTax;
  if (key !== 'amountPostTax' || amountPreTax == null || typeof postTax !== 'number') {
    return undefined;
  }
  const share = allocation.amounts[SHARE_KEY];
  // Compared in pence, so the figure shown beside the share can be typed in as it stands
  const cap = typeof share === 'number' ? shareInPounds(amountPreTax, share) : amountPreTax;
  if (Math.round(postTax * 100) <= Math.round(cap * 100)) return undefined;
  return typeof share === 'number'
    ? 'Cannot be more than their share of the amount before tax'
    : 'Cannot be more than the amount before tax';
}

/**
 * The people's after-tax amounts are each their own take-home from the income, so together they
 * cannot come to more than the whole of it before tax either. Compared in pence, as each is.
 */
export function postTaxTotalError(
  allocations: Allocation[],
  amountPreTax: number | null,
): string | undefined {
  // With every share recorded, each person's own cap at their share already bounds the total, within
  // the hundredth per owner the backend forgives on shares; the total needs its own check only without
  if (
    amountPreTax == null ||
    allocations.every(allocation => typeof allocation.amounts[SHARE_KEY] === 'number')
  ) {
    return undefined;
  }
  const totalInPence = allocations
    .map(allocation => allocation.amounts.amountPostTax)
    .filter((amount): amount is number => typeof amount === 'number')
    .reduce((total, amount) => total + Math.round(amount * 100), 0);
  return totalInPence > Math.round(amountPreTax * 100)
    ? "The people's after-tax amounts together cannot be more than the amount before tax"
    : undefined;
}

/**
 * Why the backend refused an item's owners, such as shares over 100%, where that is why a save
 * failed. The rule is the backend's alone, and it answers 422 with the reason as the detail.
 */
export function ownersRefusal(error: unknown): string | undefined {
  return error instanceof ApiError && error.status === 422 ? error.message : undefined;
}

export function storedClientIdsOf(owners: CashflowOwner[] | undefined): string[] {
  return (owners ?? [])
    .map(owner => owner.clientId)
    .filter((clientId): clientId is string => !!clientId);
}

/** Everyone a saved item is held by, by name, for the warning that deleting it removes it for them. */
export function storedOwnerNames(
  owners: CashflowOwner[] | undefined,
  nameOf: (clientId: string) => string,
): string[] {
  return (owners ?? []).map(owner =>
    owner.clientId ? nameOf(owner.clientId) : (owner.externalName ?? 'someone else'),
  );
}

/**
 * What a form opened from one client's own tab tells the adviser about a shared item: who else
 * holds it, since saving or deleting it changes it for them too, or who saving adds it to.
 * Undefined on the family tab, and for an item the client holds alone.
 *
 * Everyone the item names counts, as stored and as edited, so the notice still names a person the
 * adviser has just taken off: saving is what removes them.
 */
export function sharedWithViewer(
  viewingClientId: string | undefined,
  noun: string,
  allocations: Allocation[],
  stored: CashflowOwner[] | undefined,
  members: GroupMember[],
  nameOf: (clientId: string) => string,
): { notice: string; viewerName: string } | undefined {
  if (!viewingClientId) return undefined;
  const otherClientIds = [
    ...new Set([
      ...(stored ?? []).map(owner => owner.clientId),
      ...allocations.map(allocation => allocation.clientId),
    ]),
  ].filter((clientId): clientId is string => !!clientId && clientId !== viewingClientId);
  const outsiders = externalOwners(stored).map(owner => owner.externalName ?? 'someone else');
  if (otherClientIds.length === 0 && outsiders.length === 0) return undefined;
  // A new item added here for other people only is not this client's, so it is not shared with them
  if (!stored && !allocations.some(allocation => allocation.clientId === viewingClientId)) {
    return undefined;
  }

  const sharedWith = joinNames([...otherClientIds.map(nameOf), ...outsiders]);
  // Only clients have a Cashflows tab of their own for the item to reach
  const tabsOf = (clientIds: string[]) =>
    `${joinNames(clientIds.map(clientId => `${nameOf(clientId)}'s`))} Cashflows tab${clientIds.length > 1 ? 's' : ''}`;
  // A person the form has only just added holds no saved copy yet, so a delete from here does not
  // reach them; saving is what puts it on their tab
  const storedClientIds = new Set(storedClientIdsOf(stored));
  const holding = otherClientIds.filter(clientId => storedClientIds.has(clientId));
  const joining = otherClientIds.filter(clientId => !storedClientIds.has(clientId));
  const changes =
    holding.length > 0 ? ` Saving or deleting it here also changes it on ${tabsOf(holding)}.` : '';
  // On a saved item the form saves rather than adds it, so the sentence says so
  const savingOrAdding = stored ? 'Saving' : 'Adding';
  const adds =
    joining.length > 0 ? ` ${savingOrAdding} it here also adds it to ${tabsOf(joining)}.` : '';
  const leavesViewer =
    stored?.some(owner => owner.clientId === viewingClientId) &&
    !allocations.some(allocation => allocation.clientId === viewingClientId)
      ? ' Saving without this client takes it off their own tab.'
      : '';
  const viewer = members.find(member => member.clientId === viewingClientId);
  return {
    notice: `This ${noun} is shared with ${sharedWith}.${changes}${adds}${leavesViewer}`,
    viewerName: viewer?.firstName ?? 'This client',
  };
}
